/**
 * Shared predictive-dialer status poller (2026-09-24).
 *
 * GET /api/ai/voice/predictive/status was being polled independently by 4
 * separate components — PredictiveCohortDialer (4s), PredictiveSessionButton
 * (15s), MyChats (4s), plus a one-off read in CohortStartDialer — all mounted
 * at once for most agents. Confirmed live as the dominant driver of the
 * prelive server's CPU pin.
 *
 * This hook is the ONE poller: a single interval shared by every subscriber
 * through a module-level singleton, so N mounted components cost exactly ONE
 * network request per tick, not N independent requests at N different rates.
 *
 * ⚠️ 2026-09-24: consolidating to 1 request/s still polled EVERY agent
 * forever, including the ~majority who don't have admins.predictiveDialer
 * set at all (PredictiveSessionButton mounts unconditionally for everyone,
 * button hidden or not) — that's admin-set and never changes mid-session, so
 * there's no reason to keep asking. Once a poll comes back `enabled:false`,
 * stop polling entirely for the rest of this session — that agent gets
 * exactly ONE call, ever, instead of one every second forever. Only agents
 * who actually have the flag keep polling at POLL_MS.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();
const POLL_MS = 1000;

let latest = null;   // last response body (null until the first poll resolves)
let checked = false; // has at least one poll completed (success or failure)?
let timer = null;
let inFlight = null;
let disabledForSession = false; // enabled:false was seen — stop polling for good
const listeners = new Set();

function notify() {
  for (const fn of listeners) fn(latest, checked);
}

function pollOnce() {
  if (inFlight) return inFlight; // a poll is already in flight — reuse it, don't stack a 2nd
  inFlight = axios.get(`${AI_PATH}/api/ai/voice/predictive/status`)
    .then((r) => {
      latest = r.data;
      if (!latest?.enabled) {
        disabledForSession = true;
        if (timer) { clearInterval(timer); timer = null; }
      }
    })
    .catch(() => { latest = latest || { success: false, enabled: false }; })
    .finally(() => { checked = true; inFlight = null; notify(); });
  return inFlight;
}

function start() {
  if (timer || disabledForSession) return;
  pollOnce();
  timer = setInterval(pollOnce, POLL_MS);
}

function stop() {
  if (timer) { clearInterval(timer); timer = null; }
}

function subscribe(fn) {
  listeners.add(fn);
  start(); // no-op if disabledForSession — the cached enabled:false result below is all a late subscriber needs
  if (checked) fn(latest, checked); // replay the last known value to a late subscriber
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) stop();
  };
}

/**
 * @returns [status, isChecked, refresh]
 *   status    - raw /voice/predictive/status response body, or null before the first poll resolves
 *   isChecked - true once at least one poll has completed (mirrors each caller's old `checked` flag)
 *   refresh   - force an immediate re-poll (e.g. right after ending/submitting a call,
 *               instead of waiting up to 1s for the next tick)
 */
export default function usePredictiveStatus() {
  const [status, setStatus] = useState(latest);
  const [isChecked, setIsChecked] = useState(checked);

  useEffect(() => subscribe((val, chk) => { setStatus(val); setIsChecked(chk); }), []);

  const refresh = useCallback(() => pollOnce(), []);

  return [status, isChecked, refresh];
}
